import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { fmtMoney, fmtNumber, fmtPct } from './format';
import { btn, card } from '../../theme';

const DIMENSIONS = [
  { key: 'cost_center',        label: 'Cost Center' },
  { key: 'spend_category',     label: 'Spend Category' },
  { key: 'expense_item',       label: 'Expense Item' },
  { key: 'employee',           label: 'Employee' },
  { key: 'merchant',           label: 'Merchant' },
  { key: 'month',              label: 'Month' },
  { key: 'line_of_business',   label: 'Line of Business' },
  { key: 'city_code',          label: 'City' },
  { key: 'payment_type',       label: 'Payment Type' },
  { key: 'expense_item_group', label: 'Item Group' },
];

/**
 * BreakdownPanel — subtotals for one dimension over the current filter.
 *
 * Every subtotal set sums to the filtered grand total. When the row limit
 * truncates a long tail the remainder is shown explicitly rather than letting
 * the visible rows quietly fail to reconcile.
 *
 * Supervisor is deliberately absent from the dimension list: 517 transactions
 * carry two supervisors, so grouping by it would count those rows twice and
 * the column would not add up. Filter by manager instead — that counts each
 * row exactly once.
 */
export default function BreakdownPanel({ filterParams, onDrill, defaultDimension = 'cost_center' }) {
  // Each panel keeps its OWN dimension so two can sit side by side showing
  // different cuts of the same filtered set — hence the distinct defaults
  // supplied by the caller rather than a shared constant.
  const [dimension, setDimension] = useState(defaultDimension);
  const [limit, setLimit] = useState(15);

  const { data, isLoading } = useQuery({
    queryKey: ['expense-breakdown', dimension, filterParams, limit],
    queryFn:  () => api.get('/expenses/breakdown', { params: { ...filterParams, dimension, limit } }),
    keepPreviousData: true,
  });

  const rows   = data?.rows || [];
  const totals = data?.totals;
  const max    = Math.max(...rows.map((r) => Math.abs(r.net_amount)), 1);

  return (
    <div style={{ ...s.card, opacity: isLoading ? 0.6 : 1 }}>
      <div style={s.header}>
        {/* Title tracks the dropdown rather than being fixed by the caller, so
            it cannot go stale the moment someone switches dimension. */}
        <div style={s.title}>
          Breakdown — {DIMENSIONS.find((d) => d.key === dimension)?.label ?? dimension}
        </div>
        <select style={s.select} value={dimension} onChange={(e) => setDimension(e.target.value)}>
          {DIMENSIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
        </select>
      </div>

      <div style={s.list}>
        {rows.length === 0 && !isLoading && <div style={s.empty}>Nothing matches these filters.</div>}
        {rows.map((r) => (
          <button key={r.label} type="button" style={s.row}
                  onClick={() => onDrill && onDrill(dimension, r.label)}
                  title={`Filter to ${r.label}`}>
            <div style={s.rowTop}>
              <span style={s.rowLabel}>{r.label}</span>
              <span style={s.rowAmount}>{fmtMoney(r.net_amount)}</span>
            </div>
            <div style={s.barTrack}>
              <div style={{ ...s.barFill, width: `${(Math.abs(r.net_amount) / max) * 100}%`,
                            background: r.net_amount < 0 ? '#B45309' : '#1B2A4A' }} />
            </div>
            <div style={s.rowMeta}>
              <span>{fmtNumber(r.transaction_count)} txns</span>
              <span>{fmtPct(r.share)}</span>
            </div>
          </button>
        ))}
      </div>

      {data?.truncated && (
        <div style={s.remainder}>
          Showing top {limit}. Remaining {fmtMoney(data.remainder_amount)} not listed.
          <button type="button" style={s.moreBtn} onClick={() => setLimit((l) => l + 25)}>Show more</button>
        </div>
      )}

      {totals && (
        <div style={s.footer}>
          <span>Total ({fmtNumber(totals.transaction_count)} txns)</span>
          <span style={s.footerAmount}>{fmtMoney(totals.net_amount)}</span>
        </div>
      )}
    </div>
  );
}

const s = {
  card:   { ...card.sm, padding: '14px 16px', transition: 'opacity 0.15s' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '8px' },
  title:  { fontSize: '13px', fontWeight: 600, color: '#111827' },
  select: { height: '28px', fontSize: '11px', border: '1px solid #E5E7EB', borderRadius: '6px', padding: '0 6px', cursor: 'pointer', background: '#fff' },
  list:   { display: 'flex', flexDirection: 'column', gap: '9px', maxHeight: '440px', overflowY: 'auto' },
  row:    { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '2px 0', cursor: 'pointer' },
  rowTop: { display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'baseline' },
  rowLabel:  { fontSize: '12px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowAmount: { fontSize: '12px', fontWeight: 600, color: '#111827', flexShrink: 0, fontVariantNumeric: 'tabular-nums' },
  barTrack:  { height: '5px', background: '#F3F4F6', borderRadius: '3px', margin: '4px 0 2px', overflow: 'hidden' },
  barFill:   { height: '100%', borderRadius: '3px' },
  rowMeta:   { display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#9CA3AF' },
  empty:     { padding: '28px 8px', textAlign: 'center', fontSize: '12px', color: '#9CA3AF' },
  remainder: { marginTop: '10px', paddingTop: '8px', borderTop: '1px dashed #E5E7EB', fontSize: '10px', color: '#9CA3AF' },
  moreBtn:   { ...btn.linkXs, marginLeft: '6px' },
  footer:    { display: 'flex', justifyContent: 'space-between', marginTop: '10px', paddingTop: '9px', borderTop: '1px solid #E5E7EB', fontSize: '11px', fontWeight: 600, color: '#374151' },
  footerAmount: { fontVariantNumeric: 'tabular-nums' },
};
